/**
 * Brand mark — the official EGBA logo.
 *
 * The source file is a JPEG on a white background. It was decoded and
 * re-encoded as PNG with the white turned transparent, with the alpha
 * proportional to how far each pixel sits from white — a hard threshold would
 * leave the letter edges jagged. That is why the mark sits correctly on the
 * light sidebar and on the dark login panel alike.
 *
 * Files in `public/marca/`:
 *   · egba-completo.png  — wordmark + "GESTÃO DA INFORMAÇÃO / GOVERNO DO ESTADO"
 *   · egba-wordmark.png  — só o wordmark, para espaços estreitos
 */

interface Props {
  className?: string
  /** 'completo' inclui a assinatura; 'wordmark' é só EGBA. */
  variante?: 'completo' | 'wordmark'
  /** Altura em px. A largura acompanha a proporção original. */
  altura?: number
  /**
   * Em superfícies escuras o logo azul perde legibilidade. Aqui ele é clareado
   * por filtro, preservando a forma — melhor que uma segunda arte fora de sync
   * com a oficial.
   */
  paraFundoEscuro?: boolean
}

export function MarcaEgba({
  className,
  variante = 'wordmark',
  altura = 32,
  paraFundoEscuro = false,
}: Props) {
  const arquivo = variante === 'completo' ? 'egba-completo.png' : 'egba-wordmark.png'
  // Proporções reais dos arquivos gerados: 685×291 e 685×188.
  const proporcao = variante === 'completo' ? 685 / 291 : 685 / 188

  return (
    <img
      src={`/marca/${arquivo}`}
      alt="EGBA — Empresa Gráfica da Bahia"
      width={Math.round(altura * proporcao)}
      height={altura}
      className={className}
      style={
        paraFundoEscuro
          ? { filter: 'brightness(0) invert(1)', opacity: 0.95 }
          : undefined
      }
    />
  )
}

/**
 * Nome do produto por extenso.
 *
 * O rótulo da barra lateral é a versão curta por falta de espaço — 248 px não
 * comportam "Plataforma de Gestão Ativa do Diário Oficial" sem quebrar em
 * quatro linhas. Onde há largura (login, título da aba), vale o nome inteiro.
 */
export const NOME_PLATAFORMA = 'Plataforma de Gestão Ativa do Diário Oficial'

/** Wordmark + rótulo do produto, para o topo da barra lateral. */
export function MarcaProduto({ className }: { className?: string }) {
  return (
    <div className={`flex items-center gap-3 ${className ?? ''}`}>
      <MarcaEgba altura={26} />
      <span className="h-7 w-px bg-[color:var(--color-borda)]" aria-hidden="true" />
      <span className="text-[13px] leading-tight font-medium text-slate-600">
        Diário
        <br />
        Oficial
      </span>
    </div>
  )
}
